/**
 * PostHog Event Name Constants
 * Centralized event naming to ensure consistency across the codebase
 * Use these constants instead of hardcoded strings
 */

export const POSTHOG_EVENTS = {
  // ============ AUTH & ONBOARDING ============
  LOGIN_ATTEMPTED: 'Login Attempted',
  LOGIN_COMPLETED: 'Login Completed',
  SIGNUP_ATTEMPTED: 'Signup Attempted',
  SIGNUP_COMPLETED: 'Signup Completed',
  OTP_ENTERED: 'OTP Entered',
  OTP_FAILED: 'OTP Failed',
  PHONE_NUMBER_ENTERED: 'Phone Number Entered',
  FIRST_NAME_ENTERED: 'First Name Entered',
  LAST_NAME_ENTERED: 'Last Name Entered',
  TERMS_AND_CONDITIONS_CLICKED: 'Terms and Conditions Clicked',
  SIGNUP_OTP_ENTERED: 'Signup OTP Entered',
  SIGNUP_OTP_FAILED: 'Signup OTP Failed',
  FIRST_LOGIN_AFTER_SIGNUP: 'First Login After Signup',
  LOGOUT: 'Logout',

  // ============ INTRO & FIRST IDEAS ============
  INTRO_VIDEO_PLAYED: 'Intro Video Played',
  INTRO_VIDEO_COMPLETED: 'Intro Video Completed',
  INTRO_VIDEO_SKIPPED: 'Intro Video Skipped',
  QUESTIONNAIRE_STARTED: 'First Ideas Questionnaire Started',
  QUESTIONNAIRE_QUESTION_ANSWERED: 'Questionnaire Question Answered',
  QUESTIONNAIRE_COMPLETED: 'Questionnaire Completed',
  ACTION_PLAN_VIEWED: 'Action Plan Viewed',

  // ============ KYC FLOW ============
  KYC_INITIATED: 'KYC Initiated',
  KYC_PAN_ENTERED: 'KYC PAN Entered',
  KYC_DOB_ENTERED: 'KYC DOB Entered',
  KYC_TERMS_ACCEPTED: 'KYC Terms Accepted',
  KYC_HYPERVERGE_LAUNCHED: 'KYC HyperVerge Launched',
  KYC_DOCUMENT_UPLOADED: 'KYC Document Uploaded',
  KYC_FACE_VERIFIED: 'KYC Face Verified',
  KYC_COMPLETED: 'KYC Completed',
  KYC_FAILED: 'KYC Failed',
  KYC_DECLINED: 'KYC Declined',
  KYC_CANCELLED: 'KYC Cancelled',
  ELOG_INITIATED: 'eLog Initiated',
  ELOG_AUTHENTICATION_STARTED: 'eLog Authentication Started',
  ELOG_COMPLETED: 'eLog Completed',
  ELOG_FAILED: 'eLog Failed',
  PHONE_NUMBER_MISMATCH_DETECTED: 'Phone Number Mismatch Detected',
  PRIMARY_PHONE_UPDATED: 'Primary Phone Updated',

  // ============ MUTUAL FUND BROWSING ============
  MF_PAGE_VIEWED: 'MF Page Viewed',
  FUND_SEARCH_INITIATED: 'Fund Search Initiated',
  FUND_FILTERED: 'Fund Filtered',
  FUND_SORTED: 'Fund Sorted',
  FUND_CATEGORY_SELECTED: 'Fund Category Selected',
  FUND_CARD_CLICKED: 'Fund Card Clicked',
  FUND_DETAILS_VIEWED: 'Fund Details Viewed',
  FUND_BROWSED: 'Fund Browsed',
  PERFORMANCE_CHART_VIEWED: 'Performance Chart Viewed',

  // ============ INVESTMENT FLOW ============
  INVESTMENT_TYPE_SELECTED: 'Investment Type Selected',
  LUMPSUM_AMOUNT_ENTERED: 'Lumpsum Amount Entered',
  SIP_AMOUNT_ENTERED: 'SIP Amount Entered',
  SIP_DATE_SELECTED: 'SIP Date Selected',
  AMOUNT_ENTERED: 'Amount Entered',

  // ============ MANDATE & ORDER ============
  MANDATE_CHECK_INITIATED: 'Mandate Check Initiated',
  NEW_MANDATE_REQUIRED: 'New Mandate Required',
  EXISTING_MANDATE_FOUND: 'Existing Mandate Found',
  MANDATE_SETUP_INITIATED: 'Mandate Setup Initiated',
  BANK_AUTHENTICATION_STARTED: 'Bank Authentication Started',
  MANDATE_CONFIRMED: 'Mandate Confirmed',
  ORDER_VALIDATION_SUCCESS: 'Order Validation Success',
  ORDER_VALIDATION_FAILED: 'Order Validation Failed',

  // ============ PAYMENT ============
  PAYMENT_METHOD_SELECTED: 'Payment Method Selected',
  PAYMENT_UPI_SELECTED: 'Payment UPI Selected',
  PAYMENT_NETBANKING_SELECTED: 'Payment NetBanking Selected',
  PAYMENT_INITIATED: 'Payment Initiated',
  PAYMENT_POLLING_STARTED: 'Payment Polling Started',
  PAYMENT_COMPLETED: 'Payment Completed',
  PAYMENT_SUCCESS: 'Payment Success',
  PAYMENT_FAILED: 'Payment Failed',
  INVESTMENT_CONFIRMATION_VIEWED: 'Investment Confirmation Viewed',

  // ============ ACTION PLANS / BASKETS ============
  ACTION_CENTER_CLICKED: 'Action Center Clicked',
  ACTION_PLAN_LIST_VIEWED: 'Action Plan List Viewed',
  ACTION_PLAN_CARD_CLICKED: 'Action Plan Card Clicked',
  ACTION_PLAN_DETAILS_VIEWED: 'Action Plan Details Viewed',
  SIP_INVESTMENT_OPTION_SELECTED: 'SIP Investment Option Selected',
  ONETIME_INVESTMENT_OPTION_SELECTED: 'OneTime Investment Option Selected',
  FUND_BREAKDOWN_VIEWED: 'Fund Breakdown Viewed',
  BASKET_SIP_SETUP_INITIATED: 'Basket SIP Setup Initiated',
  BASKET_ONETIME_SETUP_INITIATED: 'Basket OneTime Setup Initiated',
  BASKET_ORDER_PLACED: 'Basket Order Placed',
  SUBSEQUENT_INVESTMENT_INITIATED: 'Subsequent Investment Initiated',

  // ============ PORTFOLIO MANAGEMENT ============
  PORTFOLIO_SUMMARY_VIEWED: 'Portfolio Summary Viewed',
  HOLDINGS_TAB_CLICKED: 'Holdings Tab Clicked',
  SIPS_TAB_CLICKED: 'SIPs Tab Clicked',
  ORDERS_TAB_CLICKED: 'Orders Tab Clicked',
  INDIVIDUAL_HOLDING_CLICKED: 'Individual Holding Clicked',
  BASKET_HOLDING_CLICKED: 'Basket Holding Clicked',
  PORTFOLIO_VIEWED: 'Portfolio Viewed',

  // ============ REDEMPTION ============
  REDEEM_BUTTON_CLICKED: 'Redeem Button Clicked',
  INVEST_MORE_CLICKED: 'Invest More Clicked',
  REDEMPTION_TYPE_SELECTED: 'Redemption Type Selected',
  REDEMPTION_AMOUNT_ENTERED: 'Redemption Amount Entered',
  FULL_REDEMPTION_SELECTED: 'Full Redemption Selected',
  PARTIAL_REDEMPTION_SELECTED: 'Partial Redemption Selected',
  REDEEM_OTP_VERIFIED: 'Redeem OTP Verified',
  REDEMPTION_ORDER_PLACED: 'Redemption Order Placed',
  REDEMPTION_SUCCESS: 'Redemption Success',
  REDEMPTION_FAILED: 'Redemption Failed',

  // ============ SIP MANAGEMENT ============
  SIP_REGISTRATION_STARTED: 'SIP Registration Started',
  SIP_REGISTRATION_SUCCESS: 'SIP Registration Success',
  SIP_REGISTRATION_FAILED: 'SIP Registration Failed',
  SIP_REGISTRATION_ERROR: 'SIP Registration Error',
  SIP_CANCEL_INITIATED: 'SIP Cancel Initiated',
  SIP_CANCELLED: 'SIP Cancelled',
  SIP_CANCEL_FAILED: 'SIP Cancel Failed',

  // ============ ORDERS ============
  ORDER_FILTER_APPLIED: 'Order Filter Applied',
  ORDER_HISTORY_EXPORTED: 'Order History Exported',

  // ============ ERROR TRACKING ============
  API_ERROR: 'API Error',
  PAYMENT_ERROR: 'Payment Error',
  KYC_ERROR: 'KYC Error',
  FORM_VALIDATION_ERROR: 'Form Validation Error',
  TOKEN_EXPIRED_ERROR: 'Token Expired Error',
  NETWORK_ERROR: 'Network Error',
  COMPONENT_ERROR: 'Component Error',

  // ============ SUPPORT ============
  SUPPORT_PORTAL_VIEWED: 'Support Portal Viewed',
  SUPPORT_QUERY_TYPE_SELECTED: 'Support Query Type Selected',
  SUPPORT_PROBLEM_DESCRIBED: 'Support Problem Described',
  SUPPORT_TICKET_SUBMITTED: 'Support Ticket Submitted',
  SUPPORT_TICKET_SUCCESS: 'Support Ticket Success',
  SUPPORT_TICKET_FAILED: 'Support Ticket Failed',
  FAQ_OPENED: 'FAQ Opened',
  FAQ_SEARCH_USED: 'FAQ Search Used',
  MORE_HELP_CLICKED: 'More Help Clicked',
  CALL_ADVISOR_CLICKED: 'Call Advisor Clicked',
  SEND_MESSAGE_CLICKED: 'Send Message Clicked',

  // ============ REFERRAL ============
  REFERRAL_CODE_VIEWED: 'Referral Code Viewed',
  REFERRAL_CODE_COPIED: 'Referral Code Copied',
  REFERRAL_LINK_SHARED: 'Referral Link Shared',
  REFERRAL_REWARD_CLAIMED: 'Referral Reward Claimed',
  REFERRED_USER_LIST_VIEWED: 'Referred User List Viewed',

  // ============ TAX FILING ============
  TAX_PLAN_SELECTED: 'Tax Plan Selected',
  TAX_FORM_STARTED: 'Tax Form Started',
  TAX_DOCUMENT_UPLOADED: 'Tax Document Uploaded',
  TAX_FILING_SUBMITTED: 'Tax Filing Submitted',
  TAX_FILING_SUCCESS: 'Tax Filing Success',
  TAX_FILING_FAILED: 'Tax Filing Failed',

  // ============ INSURANCE ============
  INSURANCE_PLAN_VIEWED: 'Insurance Plan Viewed',
  INSURANCE_CALL_BOOKED: 'Insurance Call Booked',
  INSURANCE_LEARN_MORE_CLICKED: 'Insurance Learn More Clicked',
  DITTO_INTEGRATION_LAUNCHED: 'Ditto Integration Launched',

  // ============ GENERAL ============
  PAGE_VIEWED: 'Page Viewed',
  BUTTON_CLICKED: 'Button Clicked',
  LINK_CLICKED: 'Link Clicked',
  NAVIGATION_CLICKED: 'Navigation Clicked',
}

// Property name constants for consistency
export const POSTHOG_PROPERTIES = {
  // Common
  PAGE_NAME: 'page_name',
  BUTTON_NAME: 'button_name',
  LINK_NAME: 'link_name',
  NAV_ITEM: 'nav_item',
  ACTION: 'action',

  // User
  USER_ID: 'user_id',
  PHONE_NUMBER: 'phone_number',
  EMAIL: 'email',
  USER_TYPE: 'user_type',

  // KYC
  KYC_STATUS: 'kyc_status',
  KYC_REASON: 'kyc_reason',
  PAN_NUMBER: 'pan_number',
  DOB: 'dob',
  HYPERVERGE_STATUS: 'hyperverge_status',
  ELOG_STATUS: 'elog_status',

  // Investment
  FUND_NAME: 'fund_name',
  FUND_ID: 'fund_id',
  AMOUNT: 'amount',
  INVESTMENT_TYPE: 'investment_type',
  MONTHLY_AMOUNT: 'monthly_amount',
  SIP_DATE: 'sip_date',
  SIP_AMOUNT: 'sip_amount',
  UNITS: 'units',

  // Basket
  BASKET_NAME: 'basket_name',
  BASKET_ID: 'basket_id',
  PLAN_ID: 'plan_id',
  PLAN_NAME: 'plan_name',

  // Payment
  PAYMENT_METHOD: 'payment_method',
  PAYMENT_STATUS: 'payment_status',
  ORDER_NUMBER: 'order_number',
  TRANSACTION_ID: 'transaction_id',
  UPI_ID: 'upi_id',

  // Mandate
  MANDATE_ID: 'mandate_id',
  MANDATE_STATUS: 'mandate_status',
  BANK_NAME: 'bank_name',

  // Error
  ERROR_CODE: 'error_code',
  ERROR_MESSAGE: 'error_message',
  ERROR_TYPE: 'error_type',
  ERROR_SOURCE: 'error_source',

  // Category
  CATEGORY: 'category',
  ASSET_CLASS: 'asset_class',
  FILTER_TYPE: 'filter_type',
  SORT_BY: 'sort_by',

  // Support
  QUERY_TYPE: 'query_type',
  TICKET_ID: 'ticket_id',
  FAQ_QUESTION: 'faq_question',
  ADVISOR_NAME: 'advisor_name',
  ADVISOR_TYPE: 'advisor_type',

  // Referral
  REFERRAL_CODE: 'referral_code',
  SHARE_METHOD: 'share_method',

  // Tax
  TAX_PLAN: 'tax_plan',
  DOCUMENT_TYPE: 'document_type',

  // Insurance
  INSURANCE_TYPE: 'insurance_type',
  PROVIDER: 'provider',

  // Redemption
  REDEMPTION_TYPE: 'redemption_type',
  REDEMPTION_AMOUNT: 'redemption_amount',
}
